import React, {useEffect} from "react";
import {Link, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {Loader} from "../../../elements/loader/Loader";
import {MenuItem, updateSelectedMenuItem} from "../../platform/platformSlice";
import styles from "./TranscriptPage.module.css";
import {
    fetchTranscriptBotTimezone,
    fetchTranscriptById,
    fetchWidgetAppearanceSettings,
    transcriptBotTimezoneRequestStatusSelector,
    transcriptBotTimezoneSelector,
    transcriptRequestStatusSelector,
    transcriptSelector,
    widgetAppearanceSettingsRequestStatusSelector
} from "../transcriptSlice";
import {ChatHistory} from "./chatHistory/ChatHistory";
import {TranscriptPageHeader} from "./header/TranscriptPageHeader";
import {ChatEvents} from "./chatEvents/ChatEvents";
import {ChatEventDTO, ConversationChannel} from "../../../../api/generated";
import {showErrorToastNotification} from "../../../elements/toastNotification/toastNotificationSlice";
import {FEEDBACK_SOURCE} from "../../feedback/feedbackTranscriptLink";

const toOptionalNumber = (value: string | null): number | undefined => {
    const parsed = Number(value);
    return value && Number.isFinite(parsed) ? parsed : undefined;
};

export const TranscriptPage: React.FC = () => {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const transcript = useAppSelector(transcriptSelector);
    const transcriptRequestStatus = useAppSelector(transcriptRequestStatusSelector);
    const widgetAppearanceSettingsRequestStatus = useAppSelector(widgetAppearanceSettingsRequestStatusSelector);
    const botTimezone = useAppSelector(transcriptBotTimezoneSelector);
    const botTimezoneRequestStatus = useAppSelector(transcriptBotTimezoneRequestStatusSelector);

    const {publicBotId, userId} = useParams<{ publicBotId?: string, userId?: string }>();
    const conversationChannel = searchParams.get("conversationChannel") as ConversationChannel
    const isOpenedFromFeedback = searchParams.get("from") === FEEDBACK_SOURCE;
    const focusedMessageId = toOptionalNumber(searchParams.get("message"));
    const focusedSurveyId = toOptionalNumber(searchParams.get("survey"));

    useEffect(() => {
        if (publicBotId && userId && conversationChannel) {
            dispatch(fetchTranscriptById({userId: Number(userId), conversationChannel}));
            dispatch(fetchWidgetAppearanceSettings(publicBotId));
            dispatch(fetchTranscriptBotTimezone(publicBotId));
            dispatch(updateSelectedMenuItem(isOpenedFromFeedback ? MenuItem.FEEDBACK : MenuItem.TRANSCRIPTS))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publicBotId, userId]);

    useEffect(() => {
        if (transcriptRequestStatus === RequestStatus.REJECTED ||
            widgetAppearanceSettingsRequestStatus === RequestStatus.REJECTED ||
            botTimezoneRequestStatus === RequestStatus.REJECTED) {
            navigate("/transcripts")
            dispatch(showErrorToastNotification({message: "Failed to load transcript page"}))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transcriptRequestStatus, widgetAppearanceSettingsRequestStatus, botTimezoneRequestStatus]);

    if (transcriptRequestStatus === RequestStatus.RESOLVED &&
        widgetAppearanceSettingsRequestStatus === RequestStatus.RESOLVED &&
        botTimezoneRequestStatus === RequestStatus.RESOLVED &&
        transcript) {

        const user = transcript.chatHistoryList[0].user;

        const chatEvents = Array.from(
            new Map(
                transcript.chatHistoryList
                    .flatMap(ch => ch.conversation?.chatEvents ?? [])
                    .map(ce => [ce.id, ce] as const)
            ).values()
        ) as ChatEventDTO[];

        return <div className={styles.wrapper}>
            {isOpenedFromFeedback && <Link to="/feedbacks" className={styles.backLink}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M19 12H5"/>
                    <path d="M11 18l-6-6 6-6"/>
                </svg>
                Back to Feedbacks
            </Link>}
            <TranscriptPageHeader
                user={transcript.chatHistoryList[0].user}
                conversationChannel={transcript.chatHistoryList[0].conversation.channel}
                bot={transcript.bot}
                assistantId={publicBotId}
            />
            <div className={styles.contentWrapper}>
                <ChatHistory
                    chatHistoryList={transcript.chatHistoryList}
                    emailSendingLogs={transcript.emailSendingLogs ?? []}
                    messageFeedback={transcript.messageFeedback ?? []}
                    surveys={transcript.surveys ?? []}
                    focusedMessageId={focusedMessageId}
                    focusedSurveyId={focusedSurveyId}
                />
                <ChatEvents
                    user={user}
                    chatEvents={chatEvents}
                    timezone={botTimezone}
                />
            </div>
        </div>
    } else {
        return <Loader/>
    }
}
